import { useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, TextField, Box, CircularProgress } from '@mui/material';
import { sendServerRequest } from '../../server/request';
import { URLS } from '../../env/urls';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';
import { useUserPermissions } from '../../user/useUserPermissions';

interface ClientData {
  id: number;
  name?: string;
  phone?: string;
  whatsappId?: string;
  instagramId?: string;
  instagramUsername?: string;
  telegramId?: string;
  telegramUsername?: string;
  cpf?: string;
  email?: string;
  birthDate?: string;
  motherName?: string;
  rg?: string;
  pix?: string;
  pixType?: string;
  isValided?: boolean;
  gender?: string;
}

interface EditData {
  name?: string;
  email?: string;
  birthDate?: string;
  motherName?: string;
  rg?: string;
  pix?: string;
  pixType?: string;
  gender?: string;
}

export function ClientInfo({ chatId }: { chatId: number }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<ClientData | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<EditData>({});
  const permissions = useUserPermissions();

  const canEdit = permissions?.can_edit_client_data;

  return (
    <>
      <Button fullWidth variant="text" onClick={load}>Ver dados do cliente</Button>
      <Dialog open={open} onClose={() => { setOpen(false); setIsEditing(false); }} fullWidth maxWidth="xs">
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Dados do cliente</span>
            {canEdit && data && !isEditing && (
              <Button size="small" variant="outlined" onClick={() => {
                setIsEditing(true);
                setEditData({
                  name: data.name,
                  email: data.email,
                  birthDate: data.birthDate,
                  motherName: data.motherName,
                  rg: data.rg,
                  pix: data.pix,
                  pixType: data.pixType,
                  gender: data.gender,
                });
              }}>
                Editar
              </Button>
            )}
          </div>
        </DialogTitle>
        <DialogContent>
          {loading ? <CircularLoading /> : data ? (
            isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                <TextField
                  label="Nome"
                  value={editData.name || ''}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="E-mail"
                  value={editData.email || ''}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  fullWidth
                  size="small"
                  type="email"
                />
                <TextField
                  label="Data de Nascimento (YYYY-MM-DD)"
                  value={editData.birthDate || ''}
                  onChange={(e) => setEditData({ ...editData, birthDate: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Nome da Mãe"
                  value={editData.motherName || ''}
                  onChange={(e) => setEditData({ ...editData, motherName: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="RG"
                  value={editData.rg || ''}
                  onChange={(e) => setEditData({ ...editData, rg: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Chave PIX"
                  value={editData.pix || ''}
                  onChange={(e) => setEditData({ ...editData, pix: e.target.value })}
                  fullWidth
                  size="small"
                />
                <TextField
                  select
                  label="Tipo PIX"
                  value={editData.pixType || ''}
                  onChange={(e) => setEditData({ ...editData, pixType: e.target.value })}
                  fullWidth
                  size="small"
                  SelectProps={{ native: true }}
                >
                  <option value="">Selecione o tipo</option>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="email">E-mail</option>
                  <option value="phone">Telefone</option>
                  <option value="random">Chave aleatória</option>
                </TextField>
                <TextField
                  select
                  label="Gênero"
                  value={editData.gender || ''}
                  onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                  fullWidth
                  size="small"
                  SelectProps={{ native: true }}
                >
                  <option value="">Selecione o gênero</option>
                  <option value="male">Masculino</option>
                  <option value="female">Feminino</option>
                </TextField>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={saveData}
                    disabled={saving}
                    fullWidth
                  >
                    {saving ? <CircularProgress size={24} /> : 'Salvar'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditing(false)}
                    fullWidth
                  >
                    Cancelar
                  </Button>
                </Box>
              </Box>
            ) : (
              <div>
                <Row label="Nome" value={data.name} />
                <Row label="Telefone" value={data.phone} />
                <Row label="WhatsApp ID" value={data.whatsappId} />
                {data.instagramUsername && <Row label="Instagram @" value={`@${data.instagramUsername}`} />}
                {data.instagramId && <Row label="Instagram ID" value={data.instagramId} />}
                {data.telegramUsername && <Row label="Telegram @" value={`@${data.telegramUsername}`} />}
                {data.telegramId && <Row label="Telegram ID" value={data.telegramId} />}
                <Row label="CPF" value={data.cpf} />
                <Row label="Nascimento" value={data.birthDate} />
                <Row label="Nome da mãe" value={data.motherName} />
                <Row label="E-mail" value={data.email} />
                <Row label="RG" value={data.rg} />
                <Row label="PIX" value={data.pix} />
                <Row label="Tipo PIX" value={data.pixType} />
                <Row label="Sexo" value={formatGender(data.gender)} />
                <Row label="Validado" value={data.isValided ? 'Sim' : 'Não'} />
              </div>
            )
          ) : <p>Sem dados.</p>}
        </DialogContent>
      </Dialog>
    </>
  );

  function load() {
    setOpen(true);
    if (data) return;
    setLoading(true);
    sendServerRequest('GET', URLS.fgtsChat.clientInfo(chatId)).then((e) => {
      setData(e.data?.data?.client);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
      openGlobalSnack('Não foi possível carregar os dados do cliente.', 'error');
    });
  }

  function saveData() {
    if (!data) return;
    setSaving(true);
    sendServerRequest('POST', URLS.fgtsChat.updateClientData, {
      chatId,
      ...editData,
    }).then((e) => {
      setData(e.data?.data?.client);
      setIsEditing(false);
      setSaving(false);
      openGlobalSnack('Dados atualizados com sucesso!', 'success');
    }).catch((err) => {
      setSaving(false);
      openGlobalSnack(err?.response?.data?.message || 'Erro ao atualizar dados.', 'error');
    });
  }
}

function Row({ label, value }: { label: string; value?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid #00000014' }}>
      <span style={{ color: '#00000099' }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{value || '—'}</span>
    </div>
  );
}

function formatGender(gender?: string): string {
  if (!gender) return '—';
  return gender === 'male' ? 'Masculino' : gender === 'female' ? 'Feminino' : gender;
}
